import type { EnvelopeStatus, FieldType, RecipientStatus } from "./types";

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
export function when(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.round((d.getTime() - Date.now()) / 60000);
  if (Math.abs(mins) < 60) return mins === 0 ? "just now" : rtf.format(mins, "minute");
  const hours = Math.round(mins / 60);
  if (Math.abs(hours) < 24) return rtf.format(hours, "hour");
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 7) return rtf.format(days, "day");
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
export const fullDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
export const shortDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

export function bytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export const STATUS: Record<EnvelopeStatus, { label: string; tone: "muted" | "accent" | "good" | "warn" | "bad" }> = {
  draft: { label: "Draft", tone: "muted" },
  sent: { label: "Waiting for others", tone: "accent" },
  completing: { label: "Finishing", tone: "accent" },
  completed: { label: "Completed", tone: "good" },
  declined: { label: "Declined", tone: "bad" },
  voided: { label: "Voided", tone: "warn" },
  expired: { label: "Expired", tone: "warn" },
};

export const RECIPIENT_STATUS: Record<RecipientStatus, string> = {
  pending: "Not sent yet",
  notified: "Email sent",
  viewed: "Viewed",
  signed: "Signed",
  declined: "Declined",
};

export const FIELD_LABEL: Record<FieldType, string> = {
  signature: "Signature",
  initials: "Initials",
  dateSigned: "Date signed",
  name: "Full name",
  text: "Text",
  checkbox: "Checkbox",
};

/** Size of a new field in PDF points (the server stores fractions of the page). */
export const DEFAULT_SIZES: Record<FieldType, { w: number; h: number }> = {
  signature: { w: 160, h: 44 },
  initials: { w: 64, h: 44 },
  dateSigned: { w: 110, h: 20 },
  name: { w: 150, h: 20 },
  text: { w: 150, h: 20 },
  checkbox: { w: 16, h: 16 },
};

export const rColor = (i: number) => `var(--r${((i % 10) + 10) % 10})`;

export const EVENT_LABEL: Record<string, string> = {
  created: "Envelope created",
  document_uploaded: "Document uploaded",
  sent: "Sent for signature",
  email_sent: "Signing email sent",
  link_opened: "Opened the signing link",
  code_sent: "Verification code emailed",
  code_verified: "Verified with emailed code",
  code_failed: "Entered a wrong code",
  code_locked: "Locked out after wrong codes",
  consented: "Agreed to sign electronically",
  viewed: "Viewed the document",
  signed: "Signed",
  declined: "Declined to sign",
  reminded: "Reminder sent",
  recipient_corrected: "Email address corrected",
  voided: "Voided",
  expired: "Expired",
  completed: "Completed and sealed",
  final_emailed: "Signed copy emailed",
  final_downloaded: "Signed copy downloaded",
  bounced: "Email bounced",
  complained: "Email marked as spam",
};

export const newId = (prefix: string) => `${prefix}-${crypto.randomUUID().slice(0, 13)}`;
